import { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Target, Flame, ToggleLeft, ToggleRight } from 'lucide-react';
import ModuleContainer from '../../layout/ModuleContainer.jsx';
import InfoButton from '../../common/InfoButton.jsx';

// ─── Readiness Gauge ──────────────────────────────────────────────────────────

function ReadinessGauge({ score, totalTrades }) {
  const color = score >= 70 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const label = score >= 70 && totalTrades >= 20 ? 'READY FOR LIVE' : score >= 50 ? 'BUILDING CONFIDENCE' : 'NOT READY';
  const labelColor = score >= 70 && totalTrades >= 20 ? 'text-korinth-accent' : score >= 50 ? 'text-korinth-warning' : 'text-korinth-danger';
  const circumference = 2 * Math.PI * 52;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="#1f2937" strokeWidth="10" />
          <circle cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-mono font-bold text-white">{score}</span>
          <span className="text-[9px] text-korinth-muted">READINESS</span>
        </div>
      </div>
      <span className={`text-xs font-mono font-bold mt-2 ${labelColor}`}>{label}</span>
      <InfoButton title="Readiness Score">
        <p>Calculated from your last 20 shadow trades:</p>
        <p><b>Win Rate</b> (30% weight) — % of profitable trades</p>
        <p><b>Avg R:R</b> (20% weight) — average risk/reward ratio</p>
        <p><b>Discipline</b> (30% weight) — % of trades following signals</p>
        <p><b>Streak</b> (20% weight) — consecutive wins</p>
        <p>Threshold: 20+ trades, &gt;55% win rate, &gt;1.5 R:R = READY FOR LIVE</p>
      </InfoButton>
    </div>
  );
}

// ─── Equity Curve (simple bar chart) ──────────────────────────────────────────

function EquityCurve({ trades }) {
  if (!trades || trades.length === 0) return null;
  let cumulative = 0;
  const points = trades.map(t => {
    cumulative += (t.pnl_points || 0);
    return cumulative;
  }).reverse();

  const max = Math.max(...points.map(Math.abs), 1);

  return (
    <div className="mt-4">
      <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2 flex items-center">
        Shadow Equity Curve
        <InfoButton title="Equity Curve">
          <p>Running total of your shadow trade P&L in points. Green = profitable, red = losing. A steady upward curve suggests consistency.</p>
        </InfoButton>
      </h4>
      <div className="flex items-end gap-px h-20">
        {points.map((p, i) => (
          <div key={i} className="flex-1 flex flex-col justify-end">
            <div
              className={`w-full rounded-t ${p >= 0 ? 'bg-korinth-accent' : 'bg-korinth-danger'}`}
              style={{ height: `${(Math.abs(p) / max) * 100}%`, minHeight: '2px' }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[8px] text-korinth-muted font-mono mt-0.5">
        <span>oldest</span>
        <span>latest</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ShadowTraderTab({ instrument, prefillSignal }) {
  const [readiness, setReadiness] = useState(null);
  const [trades, setTrades] = useState([]);
  const [showForm, setShowForm] = useState(!!prefillSignal);
  const [shadowMode, setShadowMode] = useState(true);
  const [form, setForm] = useState({
    direction: prefillSignal?.direction || 'LONG',
    entry_price: prefillSignal?.entry_price || '',
    exit_price: '',
    pnl_points: '',
    followed_signal: true,
    notes: '',
    emotional_state: 'confident',
  });

  useEffect(() => { fetchData(); }, [instrument]);

  useEffect(() => {
    if (prefillSignal) {
      setShowForm(true);
      setForm(f => ({ ...f, direction: prefillSignal.direction || 'LONG', entry_price: prefillSignal.entry_price || '' }));
    }
  }, [prefillSignal]);

  async function fetchData() {
    try {
      const [rRes, hRes] = await Promise.all([
        fetch('/api/futures-signals/readiness'),
        fetch(`/api/futures-signals/history?instrument=${instrument}&limit=30`),
      ]);
      if (rRes.ok) setReadiness(await rRes.json());
      if (hRes.ok) setTrades(await hRes.json());
    } catch { /* silent */ }
  }

  async function submitTrade(e) {
    e.preventDefault();
    try {
      const res = await fetch('/api/futures-signals/shadow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signal_id: prefillSignal?.id || null,
          instrument,
          ...form,
          entry_price: parseFloat(form.entry_price) || null,
          exit_price: parseFloat(form.exit_price) || null,
          pnl_points: parseFloat(form.pnl_points) || null,
          pnl_dollars: null,
          followed_signal: form.followed_signal,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setReadiness(result.readiness);
        setShowForm(false);
        setForm({ direction: 'LONG', entry_price: '', exit_price: '', pnl_points: '', followed_signal: true, notes: '', emotional_state: 'confident' });
        fetchData();
      }
    } catch { /* silent */ }
  }

  const shadowTrades = trades.filter(t => t.pnl_points != null);

  return (
    <div className="space-y-6">
      {/* Shadow Mode Toggle */}
      <div className="flex items-center justify-between bg-korinth-surface border border-korinth-border rounded-lg px-4 py-3">
        <div>
          <span className="text-sm font-mono font-bold text-white">Shadow Trading Mode</span>
          <p className="text-[10px] text-korinth-muted">Practice trades without real money — build conviction first</p>
        </div>
        <button onClick={() => setShadowMode(!shadowMode)} className="flex items-center gap-2">
          {shadowMode
            ? <ToggleRight className="w-8 h-8 text-korinth-accent" />
            : <ToggleLeft className="w-8 h-8 text-korinth-muted" />}
          <span className={`text-xs font-mono font-bold ${shadowMode ? 'text-korinth-accent' : 'text-korinth-muted'}`}>
            {shadowMode ? 'ON' : 'OFF'}
          </span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Readiness Gauge */}
        <div className="flex flex-col items-center gap-4">
          <ReadinessGauge score={readiness?.score || 0} totalTrades={readiness?.totalTrades || 0} />
          <div className="grid grid-cols-2 gap-3 w-full text-center">
            {[
              { icon: Trophy, label: 'Win Rate', value: `${readiness?.winRate || 0}%`, color: 'text-korinth-accent' },
              { icon: TrendingUp, label: 'Avg R:R', value: readiness?.avgRR || '0', color: 'text-blue-400' },
              { icon: Target, label: 'Discipline', value: `${readiness?.disciplineRate || 0}%`, color: 'text-korinth-warning' },
              { icon: Flame, label: 'Streak', value: readiness?.streak || '0', color: 'text-orange-400' },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="bg-korinth-bg rounded px-2 py-2 border border-korinth-border/30">
                <Icon className={`w-3 h-3 ${color} mx-auto mb-1`} />
                <div className={`text-sm font-mono font-bold ${color}`}>{value}</div>
                <div className="text-[8px] text-korinth-muted">{label}</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-korinth-muted text-center">{readiness?.recommendation || 'Start shadow trading to build your readiness score.'}</p>
          <EquityCurve trades={shadowTrades} />
        </div>

        {/* Center: Trade Form */}
        <ModuleContainer title="Log Shadow Trade" subtitle="Record your practice entries and exits">
          <form onSubmit={submitTrade} className="space-y-3">
            <div className="flex gap-2">
              {['LONG', 'SHORT'].map(dir => (
                <button key={dir} type="button" onClick={() => setForm(f => ({ ...f, direction: dir }))}
                  className={`flex-1 py-2 rounded text-xs font-mono font-bold ${
                    form.direction === dir
                      ? dir === 'LONG' ? 'bg-korinth-accent/20 text-korinth-accent border border-korinth-accent' : 'bg-korinth-danger/20 text-korinth-danger border border-korinth-danger'
                      : 'bg-korinth-bg text-korinth-muted border border-korinth-border'
                  }`}>{dir}</button>
              ))}
            </div>
            {[
              { key: 'entry_price', label: 'Entry Price' },
              { key: 'exit_price', label: 'Exit Price' },
              { key: 'pnl_points', label: 'P&L (points)' },
            ].map(({ key, label }) => (
              <input key={key} type="number" step="any" placeholder={label} value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full px-3 py-2 bg-korinth-bg border border-korinth-border rounded text-xs font-mono text-white placeholder-korinth-muted/50 focus:border-korinth-accent outline-none" />
            ))}
            <select value={form.emotional_state} onChange={e => setForm(f => ({ ...f, emotional_state: e.target.value }))}
              className="w-full px-3 py-2 bg-korinth-bg border border-korinth-border rounded text-xs font-mono text-white focus:border-korinth-accent outline-none">
              <option value="confident">Confident</option>
              <option value="uncertain">Uncertain</option>
              <option value="fearful">Fearful</option>
              <option value="greedy">Greedy</option>
            </select>
            <textarea placeholder="Notes (what did you learn?)" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 bg-korinth-bg border border-korinth-border rounded text-xs font-mono text-white placeholder-korinth-muted/50 focus:border-korinth-accent outline-none h-20 resize-none" />
            <label className="flex items-center gap-2 text-[10px] font-mono text-korinth-muted">
              <input type="checkbox" checked={form.followed_signal} onChange={e => setForm(f => ({ ...f, followed_signal: e.target.checked }))} />
              Followed KORINTH signal
            </label>
            <button type="submit" className="w-full py-2.5 rounded bg-korinth-accent text-korinth-bg text-xs font-mono font-bold hover:bg-korinth-accent/90">
              Submit Shadow Trade
            </button>
          </form>
        </ModuleContainer>

        {/* Right: Trade History */}
        <ModuleContainer title="Shadow History" subtitle={`${readiness?.totalTrades || 0} total trades`}>
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {trades.length > 0 ? trades.map((s, i) => (
              <div key={s.id || i} className="flex items-center justify-between px-2 py-2 bg-korinth-bg rounded text-[10px] font-mono">
                <div className="flex items-center gap-2">
                  <span className={s.direction === 'LONG' ? 'text-korinth-accent' : 'text-korinth-danger'}>
                    {s.direction === 'LONG' ? '▲' : '▼'}
                  </span>
                  <span className="text-korinth-text">{s.instrument}</span>
                  <span className="text-korinth-muted">{s.score}/10</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-bold ${
                    s.status?.includes('TP') ? 'text-korinth-accent' :
                    s.status === 'STOPPED' ? 'text-korinth-danger' : 'text-korinth-muted'
                  }`}>{s.status}</span>
                  <span className="text-korinth-muted">{s.created_at?.slice(5, 10)}</span>
                </div>
              </div>
            )) : (
              <p className="text-[10px] text-korinth-muted text-center py-6">No trades yet. Start shadow trading!</p>
            )}
          </div>
        </ModuleContainer>
      </div>
    </div>
  );
}
